import { LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { applicationDebounceTime, LoadableStatus } from '@aiwyn/shared';
import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { UntypedFormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { Sort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, tap, withLatestFrom } from 'rxjs/operators';
import { EngagementListItem, EngagementStatus, NON_INTERACTIVE_ENGAGEMENT_STATUSES } from '../../../../api/models';
import { EngagementsApiService } from '../../../../api/services/engagements-api.service';
import { ROUTE_TOKEN_ENGAGEMENTS_BASE, ROUTE_TOKEN_MAIN_BASE } from '../../../../routing-constants';
import { EngagementColumn, PaginationOptions } from '../../components';
import { EngagementTrackingService } from '../../services/engagement-tracking.service';
import { RemoveSelectedEngagementData, SetNonInteractiveEngagement } from '../../state';

const COLUMN_TO_API_SORT_FIELD: Partial<Record<EngagementColumn, string>> = {
  [EngagementColumn.EXECUTED_DATE]: 'executedAt',
};

@Component({
  selector: 'app-engagements-container',
  templateUrl: './engagements-container.component.html',
  styleUrls: ['./engagements-container.component.scss'],
  standalone: false,
})
export class EngagementsContainerComponent implements OnInit {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly host = inject<ElementRef<EngagementsContainerComponent & HTMLElement>>(ElementRef);
  private readonly engagementsApiService = inject(EngagementsApiService);
  private readonly launchDarklyService = inject(LaunchDarklyService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly engagementTrackingService = inject(EngagementTrackingService);

  private readonly pageChange$ = new Subject<PageEvent>();
  private currentSort = '';

  activeSortColumn = '';
  activeSortDirection: SortDirection = '';

  isAutoDraftFeatureEnabled = toSignal(this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_ENGAGEMENT_AUTOMATED_PAYMENT));
  displayedColumns = computed(() => [
    EngagementColumn.ACCOUNT,
    EngagementColumn.ENGAGEMENT,
    EngagementColumn.PARTNER,
    EngagementColumn.START_DATE,
    EngagementColumn.EXECUTED_DATE,
    EngagementColumn.STATUS,
    EngagementColumn.SENT_DATE,
    ...(this.isAutoDraftFeatureEnabled() ? [EngagementColumn.PAYMENT_TYPE] : []),
  ]);

  searchControl = new UntypedFormControl('');
  dataSource: MatTableDataSource<EngagementListItem>;

  readonly listStatus = signal<LoadableStatus>(LoadableStatus.DEFAULT);
  readonly engagements = signal<EngagementListItem[]>([]);
  readonly highlightTerm = signal('');

  isMobile: boolean;
  resizeObserver = new ResizeObserver(() => {
    const wasMobile = this.isMobile;
    this.isMobile = this.host.nativeElement.offsetWidth < 768;

    if (this.isMobile !== wasMobile) {
      this.changeDetectorRef.detectChanges();
    }
  });

  readonly paginationOptions$ = new BehaviorSubject<PaginationOptions>({
    currentPage: 0,
    pageSize: 20,
    totalItems: 0,
    pageSizeOptions: [10, 20, 50, 100],
  });

  readonly search$ = this.searchControl.valueChanges.pipe(
    debounceTime(500),
    takeUntilDestroyed(this.destroyRef),
    withLatestFrom(this.paginationOptions$),
    tap(([searchTerm, paginationOptions]) => this.loadEngagementList(searchTerm, 0, paginationOptions.pageSize, this.currentSort)),
  );

  readonly debouncedPageChange$ = this.pageChange$.pipe(
    applicationDebounceTime(),
    takeUntilDestroyed(this.destroyRef),
    tap(({ pageIndex, pageSize }) => this.loadEngagementList(this.searchControl.value || '', pageIndex, pageSize, this.currentSort)),
  );

  constructor() {
    this.destroyRef.onDestroy(() => this.resizeObserver.disconnect());

    if (this.listStatus() !== LoadableStatus.LOADING) {
      setTimeout(() => this.changeDetectorRef.detectChanges(), 0);
    }
  }

  ngOnInit(): void {
    this.engagementTrackingService.trackEngagementListOpened();

    this.store.dispatch(new RemoveSelectedEngagementData());
    this.resizeObserver.observe(this.host.nativeElement);
    this.search$.subscribe();
    this.debouncedPageChange$.subscribe();
    this.initFromQueryParams();
  }

  private initFromQueryParams(): void {
    const { search, page, size, sort } = this.activatedRoute.snapshot.queryParams;

    const initialSearch = search || '';
    const initialPage = Number(page ?? 0);
    const initialSize = Number(size ?? this.paginationOptions$.value.pageSize);
    const initialSort = sort || '';

    if (initialSearch) {
      this.searchControl.setValue(initialSearch, { emitEvent: false });
    }

    if (initialSize !== this.paginationOptions$.value.pageSize) {
      this.paginationOptions$.next({ ...this.paginationOptions$.value, pageSize: initialSize });
    }

    if (initialSort) {
      this.currentSort = initialSort;
      const [column, direction] = initialSort.split(',');
      this.activeSortColumn = column ?? '';
      this.activeSortDirection = (direction ?? '') as SortDirection;
    }

    this.loadEngagementList(initialSearch, initialPage, initialSize, initialSort);
  }

  private loadEngagementList(search: string = '', page: number = 0, size: number = 100, sort: string = ''): void {
    this.listStatus.set(LoadableStatus.LOADING);
    this.dataSource = new MatTableDataSource([]);

    this.engagementsApiService
      .getEngagementList(search, page, size, sort)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          const { items, currentPage, total } = response;

          this.engagements.set(items);
          this.dataSource = new MatTableDataSource(items);
          this.highlightTerm.set(search);
          this.listStatus.set(LoadableStatus.LOADED);
          this.changeDetectorRef.detectChanges();

          this.paginationOptions$.next({
            ...this.paginationOptions$.value,
            totalItems: total,
            currentPage: currentPage,
            pageSize: size,
          });

          this.updateQueryParams(search, currentPage, size, sort);
        },
        error: () => {
          this.listStatus.set(LoadableStatus.ERROR);
          this.changeDetectorRef.detectChanges();
        },
      });
  }

  viewEngagement(engagement: EngagementListItem): void {
    if (NON_INTERACTIVE_ENGAGEMENT_STATUSES.includes(engagement.status as EngagementStatus)) {
      this.store.dispatch(new SetNonInteractiveEngagement({
        id: engagement.id,
        status: engagement.status as EngagementStatus,
        partnerName: engagement.partnerName,
        startDate: engagement.startDate,
      }));
    }

    this.router.navigateByUrl(`${ROUTE_TOKEN_MAIN_BASE}/${ROUTE_TOKEN_ENGAGEMENTS_BASE}/${engagement.id}`);
  }

  onPageChange(event: PageEvent): void {
    this.pageChange$.next(event);
  }

  onSortChange(sort: Sort): void {
    this.activeSortColumn = sort.active;
    this.activeSortDirection = sort.direction;
    const sortField = COLUMN_TO_API_SORT_FIELD[sort.active as EngagementColumn] ?? sort.active;
    this.currentSort = sort.direction ? `${sortField},${sort.direction}` : '';
    this.loadEngagementList(this.searchControl.value || '', 0, this.paginationOptions$.value.pageSize, this.currentSort);
  }

  private updateQueryParams(search: string, page: number, size: number, sort: string): void {
    const defaultPageSize = 20;

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        search: search || null,
        page: page || null,
        size: size !== defaultPageSize ? size : null,
        sort: sort || null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
